import { useState } from 'react';
import { Megaphone, CalendarDays, FileText, CalendarX2, GraduationCap, Bell } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { NOTICES, EVENTS, ACADEMIC_CALENDAR } from '@/data/content';

const TABS = [
  { key: 'All', Icon: Bell },
  { key: 'Admission', Icon: GraduationCap },
  { key: 'Circular', Icon: FileText },
  { key: 'Examination', Icon: FileText },
  { key: 'Holiday', Icon: CalendarX2 },
  { key: 'Notice', Icon: Megaphone },
];

const Notices = () => {
  const [tab, setTab] = useState('All');
  const list = NOTICES.filter((n) => tab === 'All' || n.tag === tab);

  return (
    <>
      <PageHero eyebrow="Notice & Events" title="Every announcement, in one place." description="Circulars, examination notices, holidays and upcoming events — updated by the school office." />

      <section className="py-24 sm:py-32" data-testid="notices-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-3" role="tablist" aria-label="Notice filters">
              {TABS.map(({ key, Icon }) => (
                <button
                  key={key}
                  data-testid={`notice-tab-${key.toLowerCase()}`}
                  onClick={() => setTab(key)}
                  role="tab"
                  aria-selected={tab === key}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${tab === key ? 'bg-navy text-cream' : 'border border-navy/15 text-navy hover:border-navy'}`}
                >
                  <Icon className="h-4 w-4" /> {key}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {list.map((n, i) => (
              <Reveal key={n.title} delay={i * 0.05}>
                <article data-testid={`notice-card-${i}`} className="h-full rounded-lg border border-navy/10 bg-white p-7 transition-all hover:-translate-y-1 hover:border-gold">
                  <div className="flex items-center justify-between gap-4">
                    <span className="rounded-full bg-crimson-light px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-crimson">{n.tag}</span>
                    <span className="text-xs font-semibold text-navy/40">{n.date}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-navy leading-snug">{n.title}</h3>
                  <p className="mt-3 text-sm text-navy/70 leading-relaxed">{n.body}</p>
                </article>
              </Reveal>
            ))}
            {list.length === 0 && <p className="text-navy/50 py-10">No {tab.toLowerCase()} notices right now. Check back soon.</p>}
          </div>
        </div>
      </section>

      <section className="bg-cream-dark py-24 sm:py-32" data-testid="events-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading number="01" eyebrow="School Calendar" title="Upcoming events" />
          <div className="mt-14 space-y-4">
            {EVENTS.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.06}>
                <div data-testid={`event-row-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-lg border border-navy/10 bg-white px-6 py-5 transition-all hover:-translate-y-0.5 hover:border-gold">
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="rounded-lg bg-navy px-4 py-2.5 text-center min-w-[72px]">
                      <span className="block font-display text-xl font-bold text-gold leading-none">{new Date(e.date).getDate()}</span>
                      <span className="block text-[10px] font-bold uppercase tracking-widest text-cream/60 mt-1">{new Date(e.date).toLocaleDateString('en-IN', { month: 'short' })}</span>
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy sm:hidden">{e.title}</h3>
                  </div>
                  <div className="flex-1">
                    <h3 className="hidden sm:block font-display text-lg font-bold text-navy">{e.title}</h3>
                    <p className="text-sm text-navy/50 flex items-center gap-2 mt-0.5"><CalendarDays className="h-3.5 w-3.5" /> {e.time} · {e.venue}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="annual-calendar">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading number="02" eyebrow="Annual Planner" title="Session 2026–27 at a glance" />
          <div className="mt-14 grid gap-px overflow-hidden rounded-lg border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-5">
            {ACADEMIC_CALENDAR.map((m, i) => (
              <div key={m.month} data-testid={`planner-month-${i}`} className="bg-white p-6 transition-colors hover:bg-gold-light/50">
                <p className="font-display text-lg font-bold text-crimson">{m.month}</p>
                <p className="mt-2 text-sm text-navy/60 leading-relaxed">{m.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Notices;
